import React, {
  ReactElement,
  useState,
  useEffect,
  useCallback,
} from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter, Route, RouteComponentProps } from 'react-router-dom';
import { connectHits } from 'react-instantsearch-dom';
import axios from 'axios';

import { isEqual } from 'lodash';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment/moment';
import { getComputedCSSVariable } from '../../../utils/cssVariableHelpers';
import {
  ALGOLIA_USERS_INDEX,
  USER_ADMIN_PREFIX,
  IMPERSONATION_PREFIX,
  BROWSER_API_PREFIX,
} from '../../../utils/constants';
import AdminURLSync from '../AdminURLSync';
import ProfilesTooltip from '../widgets/ProfilesTooltip';
import useModal from '../../../utils/hooks/useModal';
import ConfirmationModal from '../widgets/ConfirmationModal';
import useBulkSelected from '../../../utils/hooks/useBulkSelected';
import DateFilterDisplay from '../widgets/filters/display-components/DateFilterDisplay';
import BreedFilterDisplay from '../widgets/filters/display-components/BreedFilterDisplay';
import UserFilterDisplay from '../widgets/filters/display-components/UserFilterDisplay';
import FlagFullSVG from '../../svgComponents/FlagFullSVG';
import FlagEmptySVG from '../../svgComponents/FlagEmptySVG';
import CheckSVG from '../../svgComponents/CheckSVG';
import ExclamationSVG from '../../svgComponents/ExclamationSVG';
import AlertFilterDisplay from '../widgets/filters/display-components/AlertFilterDisplay';
import NotesTooltip from '../widgets/NotesTooltip';
import EyeSVG from './assets/EyeSVG';
import AccountsSVG from './assets/AccountsSVG';
import EditSVG from './assets/EditSVG';
import UserSVG from './assets/UserSVG';
import { AlgoliaSimpleFacetOptions, SearchState, UserAccount } from './interfaces';
import AdminDashboardTableHits from './AdminDashboardTableHits';
import AdminDashboardTableHit from './AdminDashboardTableHit';
import PhoneNumber from './assets/PhoneNumber';

const Hit: React.FC<UserAccount> = ({
  objectID,
  string_id,
  id,
  phone,
  phone_extn,
  fullName,
  username,
  email,
  seller_profiles,
  seller_profiles_count,
  accreditation,
  seller_status,
  notes,
  created_at,
  suspicious_reports,
  is_flagged,
  flag_id,
  roles,
  can_be_impersonated,
  selected,
  toggleSelected,
}) => {
  const [isHidden, setIsHidden] = useState(false);
  const [rowWrapperClass, setRowWrapperClass] = useState('');

  useEffect(() => {
    let newClass = '';
    if (roles.includes('seller')) {
      newClass = 'bg-row-seller';
    } else if (roles.includes('user')) {
      newClass = 'bg-row-user';
    }
    if (newClass !== rowWrapperClass) {
      setRowWrapperClass(newClass);
    }
  }, [roles, rowWrapperClass]);

  const {
    modalData,
    setModalData,
    showModal,
    setShowModal,
    modalFormState,
    setModalFormState,
    reloadPage,
  } = useModal();

  moment.updateLocale('en', {
    relativeTime: {
      future: '%s',
      past: '%s',
      s: 'a few Seconds',
      ss: '%d Seconds',
      m: 'a Minute',
      mm: '%d Minutes',
      h: 'an Hour',
      hh: '%d Hours',
      d: 'a Day',
      dd: '%d Days',
      w: 'a Week',
      ww: '%d Weeks',
      M: 'a Month',
      MM: '%d Months',
      y: 'a Year',
      yy: '%d Years',
    },
  });

  const updateModal = (type: string) => {
    switch (type) {
      case 'delete':
        setModalData({
          message: (
            <>
              <p>
                Would you like to delete this account for
                {' '}
                {fullName}
                ?
              </p>
              <ul>
                <li>The associated SellerContact(s) will be permanently deleted.</li>
                <li>
                  The associated Litters and Listings that each of
                  those SellerContact(s) may have had will be permanently deleted.
                </li>
                <li>
                  The Seller itself will be permanently deleted.
                </li>
              </ul>
            </>
          ),
          onConfirmAsync: () => {
            return new Promise((res, rej) => {
              axios.delete(`${USER_ADMIN_PREFIX}/${id}`)
                .then(({ data }) => {
                  res(data.message);
                })
                .catch((e) => {
                  // console.log(e);
                  rej(e);
                });
            });
          },
          onClose: () => {
            updateModal('');
          },
          onSuccess: () => {
            setIsHidden(true);
          },
        });
        break;
      case 'impersonate':
        setModalData({
          title: 'Impersonate User',
          message: `Would you like to impersonate ${fullName}?`,
          onConfirm: () => {
            window.location.href = `${IMPERSONATION_PREFIX}/${id}`;
          },
          onClose: () => {
            updateModal('');
          },
          onSuccess: () => {
            setIsHidden(true);
          },
        });
        break;
      case 'flag':
        setModalData({
          message: `Would you like to flag this account for ${fullName}?`,
          ModalForm: () => {
            const [additionalInfo, setAdditionalInfo] = useState('');
            useEffect(() => {
              setModalFormState({ additional_information: additionalInfo });
            }, [additionalInfo]);

            return (
              <div className="form-group">
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label htmlFor="additional-info">Additional Information</label>
                <textarea
                  className="form-control"
                  id="additional-info"
                  value={additionalInfo}
                  onChange={(e) => setAdditionalInfo(e?.target.value)}
                />
              </div>
            );
          },
          onConfirmAsync: (fields) => {
            return new Promise((res, rej) => {
              axios.post(
                `${BROWSER_API_PREFIX}user/${id}/flag`,
                fields,
              )
                .then(({ data }) => {
                  res(data.message || 'Account flagged');
                })
                .catch((e) => {
                  // console.log(e);
                  rej(e);
                });
            });
          },
          onClose: () => {
            updateModal('');
          },
          onSuccess: () => reloadPage(),
        });
        break;
      case 'un-flag':
        setModalData({
          message: `Would you like to un-flag this ad for ${fullName}?`,
          onConfirmAsync: () => {
            return new Promise((res, rej) => {
              axios.delete(`${BROWSER_API_PREFIX}suspicious-report/${flag_id}`)
                .then(({ data }) => {
                  res(data.message || 'Account un-flagged');
                })
                .catch((e) => {
                  // console.log(e);
                  rej(e);
                });
            });
          },
          onClose: () => {
            updateModal('');
          },
          onSuccess: () => reloadPage(),
        });
        break;
      default:
        setModalData({
          message: '',
          onClose: () => {},
        });
    }
  };

  // TODO: update `any`
  const values: any = [
    {
      key: 'edit-mobile',
      wrapperClass: 'd-xl-none',
      value: (
        <a
          className="edit btn btn-admin-button-gray d-flex align-items-center"
          href={`${USER_ADMIN_PREFIX}/${id}/edit`}
          aria-label={`Edit user: ${fullName}.`}
        >
          <EditSVG />
        </a>
      ),
    },
    {
      key: 'delete-mobile',
      wrapperClass: 'd-xl-none',
      value: (
        <a
          href="#"
          className="mx-2"
          aria-label={`Delete user ${fullName}.`}
          onClick={(e) => {
            e.preventDefault();
            updateModal('delete');
          }}
        >
          <FontAwesomeIcon icon={faTrashAlt} color="red" size="xs" />
        </a>
      ),
    },
    {
      key: 'account',
      value: (
        <div className="d-flex align-items-center">
          <span
            className="d-flex mr-1"
            style={{
              width: '16px',
              height: '16px',
            }}
          >
            <UserSVG />
          </span>
          <a
            className="m-0 text-dark-btn-txt font-weight-bold"
            href={`${USER_ADMIN_PREFIX}/${id}`}
          >
            {fullName}
          </a>
        </div>
      ),
    },
    {
      key: 'phone',
      value: phone
        ? (
          <PhoneNumber
            phone={phone}
            phone_extn={phone_extn}
            linkClassName="text-nowrap text-table-light-gray m-0"
            textClassName="text-nowrap text-table-light-gray m-0"
          />
        ) : (
          <></>
        ),
    },
    {
      key: 'email',
      value: (
        <a
          className="text-table-light-gray m-0"
          href={`mailto:${email}`}
        >
          {email}
        </a>
      ),
    },
    {
      key: 'seller_profiles',
      value: seller_profiles_count > 0
        ? (
          <ProfilesTooltip
            accountID={string_id}
            profiles={seller_profiles}
            seller_profiles_count={seller_profiles_count}
          />
        )
        : (
          <p className="profiles btn btn-admin-button-gray d-flex align-items-center m-0 justify-content-center p-0">
            <AccountsSVG />
            <span className="num">0</span>
          </p>
        ),
    },
    {
      key: 'created_at',
      value: created_at
        ? (
          <p className="text-table-light-gray m-0 text-lowercase">
            {moment.unix(created_at).utc().fromNow()}
          </p>
        ) : '',
    },
    {
      key: 'seller_status',
      value: (<p className={`text-table-light-gray text-capitalize m-0 ${seller_status ? '' : 'd-none'}`}>{seller_status}</p>),
    },
    {
      key: 'roles',
      value: (
        <ul className="text-table-light-gray text-capitalize list-unstyled m-0">
          {roles.map((role) => (
            <li key={`${id}-${role}`}>{role}</li>
          ))}
        </ul>
      ),
    },
    {
      key: 'accreditation',
      value: (<p className={`text-table-light-gray text-capitalize m-0 ${accreditation ? '' : 'd-none'}`}>{accreditation}</p>),
    },
    {
      key: 'reason',
      value: (
        suspicious_reports.map((suspicious_report) => (
          <div
            key={`${suspicious_report.id}-${suspicious_report.report_type}`}
            className="d-flex align-items-center mb-2"
          >
            <a
              className="text-table-light-gray m-0 mr-2 text-capitalize"
              href={suspicious_report.admin_route}
            >
              {suspicious_report.report_type}
            </a>
            {suspicious_report.acknowledge_user_id
              ? (
                <div className="d-flex justify-content-center align-items-center order border-check-green bg-light-green cleared-icon">
                  <CheckSVG color={getComputedCSSVariable('check-green')} />
                </div>
              ) : (
                <div className="d-flex justify-content-center align-items-center border border-exclamation-red cleared-icon">
                  <ExclamationSVG color={getComputedCSSVariable('exclamation-red')} />
                </div>
              )}
          </div>
        ))
      ),
    },
    {
      key: 'notes',
      value: (
        notes ? (
          <NotesTooltip
            objectID={`${objectID}-notes`}
            notes={notes}
          />
        ) : <></>
      ),
    },
    {
      key: 'impersonate',
      value: can_be_impersonated
        ? (
          <a
            href="#"
            className="mx-2"
            aria-label={`Impersonate user ${fullName}.`}
            onClick={(e) => {
              e.preventDefault();
              updateModal('impersonate');
            }}
          >
            <EyeSVG />
          </a>
        )
        : (<></>),
    },
    {
      key: 'flagged',
      value: is_flagged && flag_id
        ? (
          <a
            href="#"
            className="mx-2"
            aria-label={`Un-flag listing with name: ${fullName}.`}
            onClick={(e) => {
              e.preventDefault();
              updateModal('un-flag');
            }}
          >
            <FlagFullSVG />
          </a>
        ) : (
          <a
            href="#"
            className="mx-2"
            aria-label={`Flag listing with name: ${fullName}.`}
            onClick={(e) => {
              e.preventDefault();
              updateModal('flag');
            }}
          >
            <FlagEmptySVG />
          </a>
        ),
    },
    {
      key: 'edit',
      wrapperClass: 'd-none d-xl-table-cell',
      value: (
        <a
          className="edit btn btn-admin-button-gray d-flex align-items-center"
          href={`${USER_ADMIN_PREFIX}/${id}/edit`}
        >
          <EditSVG />
        </a>
      ),
    },
    {
      key: 'delete',
      wrapperClass: 'd-none d-xl-table-cell',
      value: (
        <a
          href="#"
          className="mx-2"
          aria-label={`Delete user ${fullName}.`}
          onClick={(e) => {
            e.preventDefault();
            updateModal('delete');
          }}
        >
          <FontAwesomeIcon icon={faTrashAlt} color="red" size="xs" />
        </a>
      ),
    },
  ];

  return (
    <>
      {!isHidden && (
        <AdminDashboardTableHit
          values={values}
          rowWrapperClass={rowWrapperClass}
          id={id}
          selected={selected}
          toggleSelected={toggleSelected}
        />
      )}
      {!isHidden && (
        <ConfirmationModal
          show={showModal}
          setShow={setShowModal}
          modalFormState={modalFormState}
          {...modalData}
        />
      )}
    </>
  );
};

const Hits: React.FC<{
  hits: any,
  selected: Array<number>,
  toggleSelected: (id: number) => void,
}> = ({
  hits,
  selected,
  toggleSelected,
}) => {
  return (
    <AdminDashboardTableHits
      hits={hits}
      Hit={Hit}
      selected={selected}
      toggleSelected={toggleSelected}
    />
  );
};

const CustomHits = connectHits(Hits);

type Props = AlgoliaSimpleFacetOptions & RouteComponentProps;

const AdminDashboardTableAccounts: React.FC<Props> = ({
  history,
  location,
  match,
  facets,
}): ReactElement => {
  const getAccountType = (browserLocation) => {
    const { search } = browserLocation;
    const query = new URLSearchParams(search);
    return query.get('type') || 'all'; // Default to 'all'
  };

  const loadInitialSearchState = (accountType) => {
    const facetFilters: Array<string|Array<string>> = [];
    if (accountType === 'sellers') {
      facetFilters.push('roles:seller');
    }
    if (accountType === 'buyers') {
      facetFilters.push(['roles:user']);
      facetFilters.push(['roles:-seller']);
    }
    if (accountType === 'applications') {
      facetFilters.push('seller_status:pending');
    }
    if (accountType === 'suspicious' || accountType === 'flagged' || accountType === 'recently-changed') {
      facetFilters.push('is_suspicious:true');
    }
    if (accountType === 'flagged') {
      facetFilters.push('is_flagged:true');
    }
    if (accountType === 'recently-changed') {
      facetFilters.push([
        'suspicious_reports.report_type:Suspicious User Updating Contact Fields',
        'suspicious_reports.report_type:Suspicious Seller Updating Contact Fields',
        'suspicious_reports.report_type:Suspicious Seller Contact Updating Contact Fields',
      ]);
    }
    return {
      configure: {
        numericFilters: [],
        facetFilters,
      },
      query: '',
    };
  };

  const [accountType, setAccountType] = useState(getAccountType(location));
  const [externalSearchState, setExternalSearchState] = useState<SearchState>(
    loadInitialSearchState(accountType),
  );

  const {
    bulkAction,
    setBulkAction,
    updateBulkSelected,
    bulkSelected,
    toggleAllSelected,
    allSelected,
    setHits,
    clearBulkSelected,
  } = useBulkSelected();

  const {
    modalData,
    setModalData,
    showModal,
    setShowModal,
    modalFormState,
    setModalFormState,
    reloadPage,
  } = useModal();

  const performBulkAction = useCallback((action: string) => {
    if (bulkSelected.length < 1) {
      setModalData({
        message: 'No accounts have been selected. Please, select one or more ads to bulk edit.',
        isSimple: true,
        onConfirm: () => {
          setModalData({
            message: '',
            onClose: () => {},
            isSimple: true,
          });
        },
        onClose: () => {
          setModalData({
            message: '',
            onClose: () => {},
            isSimple: true,
          });
        },
      });
    } else {
      switch (action) {
        case 'delete':
          setModalData({
            message: (
              <>
                <p>
                  Would you like to delete
                  {' '}
                  {bulkSelected.length}
                  {' '}
                  user accounts?
                </p>
                <ul>
                  <li>The associated SellerContact(s) will be permanently deleted.</li>
                  <li>
                    The associated Litters and Listings that each of
                    those SellerContact(s) may have had will be permanently deleted.
                  </li>
                  <li>
                    The Seller itself will be permanently deleted.
                  </li>
                </ul>
              </>
            ),
            onConfirmAsync: () => {
              return new Promise((res, rej) => {
                axios.delete(`${USER_ADMIN_PREFIX}/${bulkSelected.join(',')}`)
                  .then(({ data }) => {
                    res(data.message);
                  })
                  .catch((e) => {
                    // console.log(e);
                    rej(e);
                  });
              });
            },
            onClose: () => {
              performBulkAction('');
              setModalFormState({});
            },
            onSuccess: () => reloadPage(),
          });
          break;
        case 'flag':
          setModalData({
            message: `Would you like to flag ${bulkSelected.length} user accounts for review?`,
            ModalForm: () => {
              // TODO: Could add a debounce here to prevent additional Algolia calls
              const [additionalInfo, setAdditionalInfo] = useState('');
              useEffect(() => {
                setModalFormState({ additional_information: additionalInfo });
              }, [additionalInfo]);

              return (
                <div className="form-group">
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label htmlFor="additional-info">Additional Information</label>
                  <textarea
                    className="form-control"
                    id="additional-info"
                    value={additionalInfo}
                    onChange={(e) => setAdditionalInfo(e?.target.value)}
                  />
                </div>
              );
            },
            onConfirmAsync: (fields) => {
              return new Promise((res, rej) => {
                axios.post(
                  `${BROWSER_API_PREFIX}user/${bulkSelected.join(',')}/bulk-flag`,
                  fields,
                )
                  .then(({ data }) => {
                    res(data.message);
                  })
                  .catch((e) => {
                    // console.log(e);
                    rej(e);
                  });
              });
            },
            onClose: () => {
              performBulkAction('');
              setModalFormState({});
            },
            onSuccess: () => reloadPage(),
          });
          break;
        case 'un-flag':
          setModalData({
            message: `Would you like to un-flag ${bulkSelected.length} accounts?`,
            onConfirmAsync: () => {
              return new Promise((res, rej) => {
                axios.delete(`${BROWSER_API_PREFIX}user/${bulkSelected.join(',')}/bulk-unflag`)
                  .then(({ data }) => {
                    res(data.message);
                  })
                  .catch((e) => {
                    // console.log(e);
                    rej(e);
                  });
              });
            },
            onClose: () => {
              performBulkAction('');
              setModalFormState({});
            },
            onSuccess: () => reloadPage(),
          });
          break;
        case 'status':
          setModalData({
            message: `Would you like to update the 'Status' of ${bulkSelected.length} users?`,
            ModalForm: () => {
              return (
                <div>
                  <Select
                    placeholder="Choose a status..."
                    onChange={(e) => {
                      if (e?.value === 'blocked') {
                        setModalFormState({ status: e?.value });
                      } else {
                        setModalFormState({ seller_status: e?.value });
                      }
                    }}
                    options={[
                      {
                        label: 'Pending',
                        value: 'pending',
                      },
                      {
                        label: 'Approved',
                        value: 'approved',
                      },
                      {
                        label: 'Blocked',
                        value: 'blocked',
                      },
                      {
                        label: 'Resubmit',
                        value: 'resubmit',
                      },
                    ]}
                  />
                </div>
              );
            },
            onConfirmAsync: (fields) => {
              return new Promise((res, rej) => {
                axios.patch(
                  `${USER_ADMIN_PREFIX}/${bulkSelected.join(',')}`,
                  fields,
                )
                  .then(({ data }) => {
                    res(data.message);
                  })
                  .catch((e) => {
                    // console.log(e);
                    rej(e);
                  });
              });
            },
            onClose: () => {
              performBulkAction('');
              setModalFormState({});
            },
            onSuccess: () => reloadPage(),
          });
          break;
        case 'roles':
          setModalData({
            message: `Would you like to update the roles of ${bulkSelected.length} user accounts?`,
            ModalForm: () => {
              return (
                <div>
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label
                    aria-hidden={true}
                    className="font-weight-bold"
                  >
                    Choose one or more roles for these users
                  </label>
                  <Select
                    placeholder="Chose roles..."
                    onChange={(e) => {
                      setModalFormState({ roles: e });
                    }}
                    options={[
                      {
                        label: 'Super',
                        value: 'super',
                      },
                      {
                        label: 'Admin',
                        value: 'admin',
                      },
                      {
                        label: 'Manager',
                        value: 'manager',
                      },
                      {
                        label: 'Editor',
                        value: 'editor',
                      },
                      {
                        label: 'Seller',
                        value: 'seller',
                      },
                      {
                        label: 'User',
                        value: 'user',
                      },
                    ]}
                    isMulti={true}
                  />
                </div>
              );
            },
            onConfirmAsync: (fields) => {
              const roles = fields.roles
                .map((field: { label: string, value: string }) => field.value);
              return new Promise((res, rej) => {
                axios.patch(
                  `${USER_ADMIN_PREFIX}/${bulkSelected.join(',')}`,
                  { roles }
                  ,
                )
                  .then(({ data }) => {
                    res(data.message);
                  })
                  .catch((e) => {
                    // console.log(e);
                    rej(e);
                  });
              });
            },
            onClose: () => {
              performBulkAction('');
              setModalFormState({});
            },
            onSuccess: () => reloadPage(),
          });
          break;
        default:
          setModalData({
            message: '',
            onClose: () => {},
            isSimple: true,
          });
      }
    }
  }, [bulkSelected, modalFormState, reloadPage]);

  useEffect(() => {
    const { value } = bulkAction;
    if (value) {
      performBulkAction(value);
    }
  }, [bulkAction]);

  useEffect(() => {
    const type = getAccountType(location);

    if (type !== accountType) {
      setAccountType(type);
    }
  }, [location]);

  useEffect(() => {
    if (accountType === 'all') {
      const { configure } = externalSearchState;
      if (configure) {
        const { facetFilters } = configure;
        if (!isEqual(facetFilters, [])) {
          setExternalSearchState({
            ...externalSearchState,
            configure: {
              ...externalSearchState.configure,
              facetFilters: [],
            },
          });
        }
      }
    } else if (accountType === 'sellers') {
      const { configure } = externalSearchState;
      if (configure) {
        const { facetFilters } = configure;
        if (!isEqual(facetFilters, ['roles:seller'])) {
          setExternalSearchState({
            ...externalSearchState,
            configure: {
              ...externalSearchState.configure,
              facetFilters: ['roles:seller'],
            },
          });
        }
      }
    } else if (accountType === 'buyers') {
      const { configure } = externalSearchState;
      if (configure) {
        const { facetFilters } = configure;
        if (!isEqual(facetFilters, [
          ['roles:user'],
          ['roles:-seller'],
        ])) {
          setExternalSearchState({
            ...externalSearchState,
            configure: {
              ...externalSearchState.configure,
              facetFilters: [
                ['roles:user'],
                ['roles:-seller'],
              ],
            },
          });
        }
      }
    } else if (accountType === 'applications') {
      const { configure } = externalSearchState;
      if (configure) {
        const { facetFilters } = configure;
        if (!isEqual(facetFilters, ['seller_status:pending'])) {
          setExternalSearchState({
            ...externalSearchState,
            configure: {
              ...externalSearchState.configure,
              facetFilters: ['seller_status:pending'],
            },
          });
        }
      }
    } else if (accountType === 'suspicious') {
      const { configure } = externalSearchState;
      if (configure) {
        const { facetFilters } = configure;
        if (!isEqual(facetFilters, ['is_suspicious:true'])) {
          setExternalSearchState({
            ...externalSearchState,
            configure: {
              ...externalSearchState.configure,
              facetFilters: ['is_suspicious:true'],
            },
          });
        }
      }
    } else if (accountType === 'flagged') {
      const { configure } = externalSearchState;
      if (configure) {
        const { facetFilters } = configure;
        if (!isEqual(facetFilters, ['is_suspicious:true', 'is_flagged:true'])) {
          setExternalSearchState({
            ...externalSearchState,
            configure: {
              ...externalSearchState.configure,
              facetFilters: ['is_suspicious:true', 'is_flagged:true'],
            },
          });
        }
      }
    } else if (accountType === 'recently-changed') {
      const { configure } = externalSearchState;
      if (configure) {
        const { facetFilters } = configure;
        if (!isEqual(facetFilters, [
          'is_suspicious:true',
          [
            'suspicious_reports.report_type:Suspicious User Updating Contact Fields',
            'suspicious_reports.report_type:Suspicious Seller Updating Contact Fields',
            'suspicious_reports.report_type:Suspicious Seller Contact Updating Contact Fields',

          ],
        ])) {
          setExternalSearchState({
            ...externalSearchState,
            configure: {
              ...externalSearchState.configure,
              facetFilters: [
                'is_suspicious:true',
                [
                  'suspicious_reports.report_type:Suspicious User Updating Contact Fields',
                  'suspicious_reports.report_type:Suspicious Seller Updating Contact Fields',
                  'suspicious_reports.report_type:Suspicious Seller Contact Updating Contact Fields',

                ],
              ],
            },
          });
        }
      }
    }
  }, [accountType]);

  const tableHeaderRowValues = [
    {
      key: 'account',
      value: 'Account',
      sort: {
        asc: `${ALGOLIA_USERS_INDEX}_name_az`,
        desc: `${ALGOLIA_USERS_INDEX}_name_za`,
      },
    },
    {
      key: 'phone',
      value: 'Phone',
    },
    {
      key: 'email',
      value: 'Email',
      sort: {
        asc: `${ALGOLIA_USERS_INDEX}_email_asc`,
        desc: `${ALGOLIA_USERS_INDEX}_email_desc`,
      },
    },
    {
      key: 'profiles',
      value: 'Profiles',
      sort: {
        desc: `${ALGOLIA_USERS_INDEX}_profiles_desc`,
      },
    },
    {
      key: 'created_at',
      value: 'Member For',
      sort: {
        // These are flipped, since it's to sort by time ago, but the value is a timestamp
        asc: ALGOLIA_USERS_INDEX,
        desc: `${ALGOLIA_USERS_INDEX}_created_at_asc`,
      },
    },
    {
      key: 'seller_status',
      value: 'Status',
      sort: {
        asc: `${ALGOLIA_USERS_INDEX}_seller_status_asc`,
        desc: `${ALGOLIA_USERS_INDEX}_seller_status_desc`,
      },
    },
    {
      key: 'roles',
      value: 'Roles',
    },
    {
      key: 'accreditation',
      value: 'Accreditation',
      sort: {
        asc: `${ALGOLIA_USERS_INDEX}_accreditation_asc`,
        desc: `${ALGOLIA_USERS_INDEX}_accreditation_desc`,
      },
    },
    {
      key: 'reason',
      value: 'Reason',
    },
    {
      key: 'notes',
      value: 'Notes',
    },
  ];

  return (
    <>
      <AdminURLSync
        title="Accounts"
        numEmptyStartHeaders={2}
        numEmptyEndHeaders={4}
        numUnhiddenEmptyEndHeaders={2}
        setBulkAction={setBulkAction}
        bulkAction={bulkAction}
        setHits={setHits}
        allSelected={allSelected}
        toggleAllSelected={toggleAllSelected}
        clearBulkSelected={clearBulkSelected}
        bulkActions={[
          {
            label: 'Update Status',
            value: 'status',
          },
          {
            label: 'Flag For Review',
            value: 'flag',
          },
          {
            label: 'Un-Flag For Review',
            value: 'un-flag',
          },
          {
            label: 'Delete',
            value: 'delete',
          },
          {
            label: 'Edit Roles',
            value: 'roles',
          },
        ]}
        externalQueryParams={[
          {
            key: 'type',
            value: accountType,
          },
        ]}
        history={history}
        location={location}
        match={match}
        externalSearchState={externalSearchState}
        tableHeaderRowValues={tableHeaderRowValues}
        indexName={ALGOLIA_USERS_INDEX!}
        customHits={(
          <CustomHits
            selected={bulkSelected}
            toggleSelected={updateBulkSelected}
          />
        )}
        attributes={[
          {
            type: 'dateRange',
            attribute: 'applied_at',
            startLabel: '',
            endLabel: '',
            DisplayComponent: <DateFilterDisplay title="Applied:" />,
          },
          {
            type: 'facet',
            attribute: 'breeds',
            isSearchable: true,
            isAsync: true,
            isMulti: true,
            DisplayComponent: <BreedFilterDisplay />,
            placeholder: '',
            simpleOptions: facets?.breeds || [],
          },
          {
            type: 'facet',
            attribute: 'suspicious_reports.report_type',
            label: 'Reason',
            isSearchable: false,
            isAsync: false,
            isMulti: true,
            isToggleable: false,
            DisplayComponent: <AlertFilterDisplay title="Reason:" />,
            placeholder: '',
            simpleOptions: facets?.report_type || [],
          },
          {
            type: 'facet',
            attribute: 'roles',
            isSearchable: false,
            isAsync: false,
            isMulti: true,
            DisplayComponent: <UserFilterDisplay title="Role:" />,
            placeholder: '',
            simpleOptions: facets?.roles || [],
          },
        ]}
        dateAttributes={[]}
        tabs={[
          {
            title: 'All',
            route: `${USER_ADMIN_PREFIX}?type=all`,
            key: 'type=all',
            indexName: ALGOLIA_USERS_INDEX,
            facetFilters: [],
            numericFilters: [],
          },
          {
            title: 'Sellers',
            route: `${USER_ADMIN_PREFIX}?type=sellers`,
            key: 'type=sellers',
            indexName: ALGOLIA_USERS_INDEX,
            facetFilters: ['roles:seller'],
            numericFilters: [],
          },
          {
            title: 'Buyers',
            route: `${USER_ADMIN_PREFIX}?type=buyers`,
            key: 'type=buyers',
            indexName: ALGOLIA_USERS_INDEX,
            facetFilters: [
              ['roles:user'],
              ['roles:-seller'],
            ],
            numericFilters: [],
          },
          {
            title: 'Suspicious',
            route: `${USER_ADMIN_PREFIX}?type=suspicious`,
            key: 'type=suspicious',
            indexName: ALGOLIA_USERS_INDEX,
            facetFilters: ['is_suspicious:true'],
            numericFilters: [],
          },
          {
            title: 'Flagged',
            route: `${USER_ADMIN_PREFIX}?type=flagged`,
            key: 'type=flagged',
            indexName: ALGOLIA_USERS_INDEX,
            facetFilters: ['is_suspicious:true', 'is_flagged:true'],
            numericFilters: [],
          },
          {
            title: 'Recently Changed',
            route: `${USER_ADMIN_PREFIX}?type=recently-changed`,
            key: 'type=recently-changed',
            indexName: ALGOLIA_USERS_INDEX,
            facetFilters: [
              'is_suspicious:true',
              [
                'suspicious_reports.report_type:Suspicious User Updating Contact Fields',
                'suspicious_reports.report_type:Suspicious Seller Updating Contact Fields',
                'suspicious_reports.report_type:Suspicious Seller Contact Updating Contact Fields',

              ],
            ],
            numericFilters: [],
          },
          {
            title: 'Applications',
            route: `${USER_ADMIN_PREFIX}?type=applications`,
            key: 'type=applications',
            indexName: ALGOLIA_USERS_INDEX,
            facetFilters: ['seller_status:pending'],
            numericFilters: [],
          },
        ]}
      />
      <ConfirmationModal
        show={showModal}
        setShow={setShowModal}
        modalFormState={modalFormState}
        clearBulkSelected={clearBulkSelected}
        {...modalData}
      />
    </>
  );
};

export default AdminDashboardTableAccounts;

if (document.getElementById('react-admin-dashboard-table-users-suspicious')
  || document.getElementById('react-admin-dashboard-table-users-flagged')
  || document.getElementById('react-admin-dashboard-table-users-recently-changed')) {
  const element = document.getElementById('react-admin-dashboard-table-users-suspicious')
    || document.getElementById('react-admin-dashboard-table-users-flagged')
    || document.getElementById('react-admin-dashboard-table-users-recently-changed');

  const facets = element?.dataset.facets && JSON.parse(element?.dataset.facets);

  ReactDOM.render(
    <BrowserRouter>
      <Route
        path={USER_ADMIN_PREFIX}
        render={(routeProps) => (
          <AdminDashboardTableAccounts
            facets={facets}
            {...routeProps}
          />
        )}
      />
    </BrowserRouter>,
    element,
  );
}
